import { useEffect, useRef, useState } from "react";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, FileText, Send, X, Search } from "@/components/icons";
import { useSettings } from "../lib/settings";
import { useAuth } from "../lib/auth";
import Portal from "./Portal";
import AppleModal from "./AppleModal";

const emptyItem = {
  medication: "",
  dosage: "",
  frequency: "",
  duration: "",
  quantity: "",
  instructions: "",
};

/**
 * Modal "Nueva receta" — estilo minimalista Files Management.
 *
 * Props:
 *  - patientId (string, opcional)
 *  - patientName (string, opcional)
 *  - existing (obj, opcional): al editar receta ya emitida
 *  - onClose (fn)
 *  - onSaved (fn)
 */
export default function PrescriptionModal({
  patientId = "",
  patientName = "",
  existing = null,
  onClose,
  onSaved,
}) {
  const { pharmacyEnabled } = useSettings();
  const { user } = useAuth();
  const editing = Boolean(existing?.id);

  const [patients, setPatients] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [form, setForm] = useState({
    patient_id: existing?.patient_id || patientId || "",
    diagnosis: existing?.diagnosis || "",
    notes: existing?.notes || "",
    pharmacy_ids: existing?.pharmacy_ids || [],
    items: existing?.items || [],
  });
  const [currentItem, setCurrentItem] = useState({ ...emptyItem });

  useEffect(() => {
    api.get("/patients").then((r) => setPatients(r.data));
    api
      .get("/users/pharmacies")
      .then((r) => setPharmacies(r.data))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.patient_id) {
      toast.error("Selecciona un paciente");
      return;
    }
    if (form.items.length === 0) {
      toast.error("Agrega al menos un medicamento a la receta");
      return;
    }
    try {
      if (editing) {
        await api.put(`/prescriptions/${existing.id}`, form);
        toast.success("Receta actualizada");
      } else {
        await api.post("/prescriptions", form);
        toast.success("Receta creada");
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const removeRx = async () => {
    if (!editing) return;
    if (!window.confirm("¿Eliminar esta receta?")) return;
    try {
      await api.delete(`/prescriptions/${existing.id}`);
      toast.success("Receta eliminada");
      onSaved?.();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const addCurrentItem = () => {
    if (!currentItem.medication.trim()) {
      toast.error("Ingresa el nombre del medicamento");
      return;
    }
    setForm((f) => ({ ...f, items: [...f.items, currentItem] }));
    setCurrentItem({ ...emptyItem });
  };

  const removeItem = (i) => {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, k) => k !== i),
    }));
  };

  const togglePharm = (id) => {
    setForm((f) => ({
      ...f,
      pharmacy_ids: f.pharmacy_ids.includes(id)
        ? f.pharmacy_ids.filter((x) => x !== id)
        : [...f.pharmacy_ids, id],
    }));
  };

  const patientById = Object.fromEntries(patients.map((p) => [p.id, p]));
  const selectedPatient =
    patientById[form.patient_id] ||
    (patientName ? { full_name: patientName } : null);

  // ESC cierra
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <AppleModal
      open
      onClose={onClose}
      icon={<FileText weight="regular" className="w-11 h-11" strokeWidth={1.5} />}
      title={editing ? "Editar receta" : "Nueva receta"}
      subtitle="Paciente, diagnóstico y medicamentos"
      size="xl"
      testid="rx-modal"
    >
      <form
        onSubmit={submit}
        className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6 -mx-2"
      >
        {/* LEFT: form */}
        <div className="max-h-[70vh] overflow-y-auto no-scrollbar flex flex-col px-2">

            <div className="space-y-4 flex-1">
              {/* Paciente */}
              <div>
                <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45 block mb-1.5">
                  Paciente
                </label>
                <PatientAutocomplete
                  patients={patients}
                  value={form.patient_id}
                  onChange={(id) => setForm({ ...form, patient_id: id })}
                />
              </div>

              {/* Diagnóstico */}
              <div>
                <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45 block mb-1.5">
                  Diagnóstico
                </label>
                <input
                  placeholder="Ej: Bronquitis aguda"
                  value={form.diagnosis}
                  onChange={(e) =>
                    setForm({ ...form, diagnosis: e.target.value })
                  }
                  className="w-full h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] text-black focus:outline-none focus:border-black/40 transition"
                  data-testid="rx-diagnosis-input"
                />
              </div>

              {/* Añadir medicamento */}
              <div className="bg-[#F2F3F5] rounded-lg p-3 space-y-2">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-black/45">
                  Añadir medicamento
                </div>
                <input
                  placeholder="Nombre del medicamento"
                  value={currentItem.medication}
                  onChange={(e) =>
                    setCurrentItem({
                      ...currentItem,
                      medication: e.target.value,
                    })
                  }
                  className="w-full h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] font-semibold text-black focus:outline-none focus:border-black/40 transition"
                  data-testid="rx-med-input"
                />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    placeholder="Dosis (ej: 500 mg)"
                    value={currentItem.dosage}
                    onChange={(e) =>
                      setCurrentItem({ ...currentItem, dosage: e.target.value })
                    }
                    className="h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] text-black focus:outline-none focus:border-black/40 transition"
                    data-testid="rx-dosage-input"
                  />
                  <input
                    placeholder="Cantidad (ej: 30 tabl.)"
                    value={currentItem.quantity}
                    onChange={(e) =>
                      setCurrentItem({
                        ...currentItem,
                        quantity: e.target.value,
                      })
                    }
                    className="h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] text-black focus:outline-none focus:border-black/40 transition"
                    data-testid="rx-qty-input"
                  />
                  <input
                    placeholder="Frecuencia (ej: c/8 h)"
                    value={currentItem.frequency}
                    onChange={(e) =>
                      setCurrentItem({
                        ...currentItem,
                        frequency: e.target.value,
                      })
                    }
                    className="h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] text-black focus:outline-none focus:border-black/40 transition"
                    data-testid="rx-freq-input"
                  />
                  <input
                    placeholder="Duración (ej: 7 días)"
                    value={currentItem.duration}
                    onChange={(e) =>
                      setCurrentItem({
                        ...currentItem,
                        duration: e.target.value,
                      })
                    }
                    className="h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] text-black focus:outline-none focus:border-black/40 transition"
                    data-testid="rx-dur-input"
                  />
                </div>
                <input
                  placeholder="Indicaciones (opcional)"
                  value={currentItem.instructions}
                  onChange={(e) =>
                    setCurrentItem({
                      ...currentItem,
                      instructions: e.target.value,
                    })
                  }
                  className="w-full h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] text-black focus:outline-none focus:border-black/40 transition"
                  data-testid="rx-instr-input"
                />
                <button
                  type="button"
                  onClick={addCurrentItem}
                  className="w-full h-11 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#0071E3] text-white text-[13px] font-semibold hover:bg-[#0077ED] transition"
                  data-testid="rx-add-item"
                >
                  <Plus
                    className="w-3.5 h-3.5"
                    strokeWidth={1.75}
                    weight="regular"
                  />
                  Añadir a la receta
                </button>
              </div>

              {/* Notas */}
              <div>
                <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45 block mb-1.5">
                  Notas adicionales
                </label>
                <textarea
                  placeholder="Ej: reposo 3 días, volver si fiebre persiste"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  className="w-full rounded-lg border border-black/[0.08] bg-white px-3 py-2 text-[13px] text-black focus:outline-none focus:border-black/40 transition resize-none"
                />
              </div>

              {/* Farmacias */}
              {pharmacyEnabled && (
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-2 inline-flex items-center gap-1.5">
                    <Send
                      className="w-3 h-3"
                      strokeWidth={1.75}
                      weight="regular"
                    />
                    Enviar a farmacias
                  </label>
                  <div className="space-y-1.5">
                    {pharmacies.map((p) => (
                      <label
                        key={p.id}
                        className={`flex items-center gap-2.5 p-2.5 rounded-lg cursor-pointer transition ${
                          form.pharmacy_ids.includes(p.id)
                            ? "bg-black/[0.04] border border-black/20"
                            : "bg-[#F2F3F5] border border-transparent hover:bg-black/[0.04]"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={form.pharmacy_ids.includes(p.id)}
                          onChange={() => togglePharm(p.id)}
                          data-testid={`rx-pharm-${p.id}`}
                          className="accent-black"
                        />
                        <div className="min-w-0">
                          <div className="font-semibold text-black text-[12px] truncate">
                            {p.name}
                          </div>
                          <div className="text-[11px] text-black/50 truncate">
                            {p.address}
                          </div>
                        </div>
                      </label>
                    ))}
                    {pharmacies.length === 0 && (
                      <div className="text-[12px] text-black/50">
                        No hay farmacias registradas.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-black/[0.05] bg-white sticky bottom-0">
              {editing && (
                <button
                  type="button"
                  onClick={removeRx}
                  data-testid="rx-delete-button"
                  className="mr-auto h-10 px-4 rounded-lg bg-white border border-red-200 hover:bg-red-50 text-[13px] font-semibold text-red-700 transition"
                >
                  Eliminar
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="h-11 px-5 rounded-full bg-white border border-black/15 hover:bg-black/[0.03] text-[14px] font-medium text-black transition"
              >
                Cancelar
              </button>
              <button
                type="submit"
                data-testid="rx-submit-button"
                className="h-11 px-6 rounded-full bg-[#0071E3] text-white hover:bg-[#0077ED] text-[14px] font-semibold transition"
              >
                {editing ? "Guardar cambios" : "Crear receta"}
              </button>
            </div>
          </div>

          {/* RIGHT: preview */}
          <div className="bg-[#F2F3F5] rounded-2xl p-5 max-h-[70vh] overflow-y-auto no-scrollbar">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-3">
              Vista previa de la receta
            </div>
            <RxPreview
              form={form}
              doctor={user}
              patient={selectedPatient}
              onRemoveItem={removeItem}
            />
          </div>
        </form>
    </AppleModal>
  );
}

// ─────────────────────────────────────────────────────────────────────────
function PatientAutocomplete({ patients, value, onChange }) {
  const [query, setQuery] = useState("");
  const [openList, setOpenList] = useState(false);
  const wrapRef = useRef(null);

  const selected = patients.find((p) => p.id === value);

  useEffect(() => {
    if (selected) {
      // eslint-disable-next-line
      setQuery(selected.full_name);
    } else if (!value) {
      setQuery("");
    }
  }, [selected, value]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpenList(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const q = query.trim().toLowerCase();
  const matches = q
    ? patients
        .filter(
          (p) =>
            (p.full_name || "").toLowerCase().includes(q) ||
            (p.document_id || "").toLowerCase().includes(q) ||
            (p.phone || "").toLowerCase().includes(q),
        )
        .slice(0, 8)
    : patients.slice(0, 8);

  const pick = (p) => {
    onChange(p.id);
    setQuery(p.full_name);
    setOpenList(false);
  };

  const clear = () => {
    onChange("");
    setQuery("");
    setOpenList(true);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <div className="relative">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/40 pointer-events-none"
          strokeWidth={1.75}
          weight="regular"
        />
        <input
          type="text"
          required={!value}
          placeholder="Buscar por nombre, documento o teléfono…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpenList(true);
            if (value) onChange("");
          }}
          onFocus={() => setOpenList(true)}
          className="w-full h-10 rounded-lg border border-black/[0.08] bg-white pl-9 pr-9 text-[13px] text-black focus:outline-none focus:border-black/40 transition"
          data-testid="rx-patient-search"
          autoComplete="off"
        />
        {(query || value) && (
          <button
            type="button"
            onClick={clear}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md hover:bg-black/[0.06] flex items-center justify-center transition"
            tabIndex={-1}
            data-testid="rx-patient-clear"
          >
            <X
              className="w-3.5 h-3.5 text-black/50"
              strokeWidth={1.75}
              weight="regular"
            />
          </button>
        )}
      </div>
      {openList && (
        <div className="absolute z-30 left-0 right-0 top-full mt-1.5 bg-white border border-black/[0.08] rounded-lg shadow-lg overflow-hidden max-h-72 overflow-y-auto no-scrollbar">
          {matches.length === 0 ? (
            <div className="px-3 py-4 text-[12px] text-black/50 text-center">
              Sin resultados
            </div>
          ) : (
            matches.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => pick(p)}
                data-testid={`rx-patient-opt-${p.id}`}
                className={`w-full text-left px-3 py-2 hover:bg-black/[0.04] border-b border-black/[0.04] last:border-0 transition ${
                  p.id === value ? "bg-black/[0.04]" : ""
                }`}
              >
                <div className="text-[13px] font-semibold text-black">
                  {p.full_name}
                </div>
                <div className="text-[11px] text-black/50">
                  {[p.document_id, p.phone].filter(Boolean).join(" · ") || "—"}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
function RxPreview({ form, doctor, patient, onRemoveItem }) {
  return (
    <div
      className="bg-white rounded-xl border border-black/[0.05] p-6 text-black text-[12px]"
      data-testid="rx-preview"
    >
      {/* Header */}
      <div className="flex items-start justify-between border-b border-black/10 pb-3">
        <div className="flex items-center gap-2.5">
          <img
            src={doctor?.clinic_logo_url || "/brand/consudoctor-icon.png"}
            alt="Logo"
            className="w-11 h-11 rounded-lg object-contain bg-white border border-black/[0.05]"
            data-testid="rx-preview-logo"
          />
          <div>
            <div className="text-[14px] font-semibold leading-tight text-black">
              {doctor?.name || "Dr."}
            </div>
            {doctor?.specialty && (
              <div className="text-[11px] font-semibold text-black/60">
                {doctor.specialty}
              </div>
            )}
            <div className="text-[9px] uppercase tracking-widest font-semibold text-black/45 mt-0.5">
              Receta médica
            </div>
          </div>
        </div>
        <div className="text-right text-[10px] text-black/50">
          <div className="font-semibold text-black/70">Vista previa</div>
          <div>{new Date().toLocaleString("es-ES")}</div>
        </div>
      </div>

      {/* Médico + Paciente */}
      <div className="mt-4 grid grid-cols-2 gap-4">
        <div>
          <div className="text-[9px] uppercase tracking-widest font-semibold text-black/45">
            Médico
          </div>
          <div className="font-semibold text-black">{doctor?.name}</div>
          {doctor?.specialty && (
            <div className="text-[11px] text-black/60">{doctor.specialty}</div>
          )}
          {doctor?.email && (
            <div className="text-[11px] text-black/60">{doctor.email}</div>
          )}
          {doctor?.phone && (
            <div className="text-[11px] text-black/60">Tel: {doctor.phone}</div>
          )}
          {doctor?.address && (
            <div className="text-[11px] text-black/60">{doctor.address}</div>
          )}
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest font-semibold text-black/45">
            Paciente
          </div>
          <div className="font-semibold text-black">
            {patient?.full_name || (
              <span className="text-black/40 font-normal">
                — Seleccionar paciente —
              </span>
            )}
          </div>
          {patient?.document_id && (
            <div className="text-[11px] text-black/60">
              {patient.document_id}
            </div>
          )}
          {patient?.phone && (
            <div className="text-[11px] text-black/60">{patient.phone}</div>
          )}
        </div>
      </div>

      {/* Diagnóstico */}
      <div className="mt-4">
        <div className="text-[9px] uppercase tracking-widest font-semibold text-black/45">
          Diagnóstico
        </div>
        <div className="font-semibold text-black">
          {form.diagnosis || (
            <span className="text-black/40 font-normal">—</span>
          )}
        </div>
      </div>

      {/* ℞ items */}
      <div className="mt-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="text-[26px] font-serif italic text-black/80 leading-none select-none">
            ℞
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest font-semibold text-black/45">
              Prescripción
            </div>
            <div className="text-[10px] text-black/55">
              {form.items.length} medicamento
              {form.items.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>
        {form.items.length === 0 ? (
          <div className="rounded-lg bg-[#F2F3F5] py-6 text-center text-black/40 text-[11px]">
            Aún no hay medicamentos. Usa el panel izquierdo para añadir.
          </div>
        ) : (
          <table className="w-full border-collapse text-[11px]">
            <thead>
              <tr className="border-b border-black/15 text-left">
                <th className="py-1.5 pr-2 w-6 text-[8px] uppercase tracking-widest font-semibold text-black/45">
                  #
                </th>
                <th className="py-1.5 px-2 text-[8px] uppercase tracking-widest font-semibold text-black/45">
                  Medicamento
                </th>
                <th className="py-1.5 px-2 text-[8px] uppercase tracking-widest font-semibold text-black/45">
                  Dosis
                </th>
                <th className="py-1.5 px-2 text-[8px] uppercase tracking-widest font-semibold text-black/45">
                  Cant.
                </th>
                <th className="py-1.5 px-2 text-[8px] uppercase tracking-widest font-semibold text-black/45">
                  Frec.
                </th>
                <th className="py-1.5 px-2 text-[8px] uppercase tracking-widest font-semibold text-black/45">
                  Dur.
                </th>
                <th className="py-1.5 pl-2 w-8" />
              </tr>
            </thead>
            <tbody>
              {form.items.map((it, i) => (
                <RxRow
                  key={i}
                  index={i}
                  item={it}
                  onRemove={() => onRemoveItem(i)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Notas */}
      {form.notes && (
        <div className="mt-4">
          <div className="text-[9px] uppercase tracking-widest font-semibold text-black/45">
            Indicaciones / notas
          </div>
          <div className="text-[11px] whitespace-pre-wrap text-black/80">
            {form.notes}
          </div>
        </div>
      )}

      {/* Firma */}
      <div className="mt-8 pt-4 border-t border-black/10 flex justify-between items-end text-[10px]">
        <div className="flex flex-col items-start gap-1">
          <img
            src="/brand/consudoctor-icon.png"
            alt="ConsuDoctor"
            className="h-5 w-auto object-contain"
          />
          <div className="text-black/45 font-semibold">
            www.consudoctor.com
          </div>
        </div>
        <div className="text-right">
          {doctor?.signature_url ? (
            <img
              src={doctor.signature_url}
              alt="firma"
              className="h-12 w-auto max-w-[180px] object-contain ml-auto"
            />
          ) : (
            <div className="h-12 w-40 rounded-lg bg-[#F2F3F5] flex items-center justify-center text-[10px] text-black/40 ml-auto">
              Sin firma
            </div>
          )}
          <div className="border-t border-black w-40 mt-1 ml-auto" />
          <div className="font-semibold text-black">{doctor?.name}</div>
          <div className="text-black/50">Firma del médico</div>
        </div>
      </div>
    </div>
  );
}

function RxRow({ index, item, onRemove }) {
  return (
    <>
      <tr
        className="border-b border-black/10 align-top"
        data-testid={`rx-preview-row-${index}`}
      >
        <td className="py-2 pr-2 font-semibold text-black/60">{index + 1}.</td>
        <td className="py-2 px-2 font-semibold text-black">{item.medication}</td>
        <td className="py-2 px-2 text-black/70">{item.dosage || "—"}</td>
        <td className="py-2 px-2 font-semibold text-black/80">
          {item.quantity || "—"}
        </td>
        <td className="py-2 px-2 text-black/70">{item.frequency || "—"}</td>
        <td className="py-2 px-2 text-black/70">{item.duration || "—"}</td>
        <td className="py-2 pl-2 text-right">
          <button
            type="button"
            onClick={onRemove}
            data-testid={`rx-preview-remove-${index}`}
            className="w-6 h-6 rounded-md text-black/40 hover:text-red-600 hover:bg-red-50 flex items-center justify-center transition"
            title="Quitar"
          >
            <Trash2
              className="w-3.5 h-3.5"
              strokeWidth={1.75}
              weight="regular"
            />
          </button>
        </td>
      </tr>
      {item.instructions && (
        <tr className="border-b border-black/10">
          <td></td>
          <td colSpan={6} className="pb-2 px-2 text-[10px] italic text-black/60">
            Indicaciones: {item.instructions}
          </td>
        </tr>
      )}
    </>
  );
}
